import React, { type ReactNode, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Package, ArrowUp, ArrowDown, Pencil, Trash2, Check, X, Percent, Tag, BookOpen, Layers,
  Camera, Book, Clapperboard, Film, Smartphone, Zap, Radio, Heart, Sunset,
  Printer, Image, Palette, Save, User, Video, Circle, Truck,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPatch, apiDelete, apiPut } from '../../../api/client';
import type { Event, EventService, ServicePackage, ServiceType, DiscountType, ProductTemplate, Supplier, SupplierProduct } from '../../../api/types';
import { renderCatalogIcon } from '../../../utils/renderCatalogIcon.js';
import { resolveServiceType, resolveCategoryIcon } from '../../../utils/categoryMapping';
import { useProductCategories } from '../../../api/useProductCategories';
import { useSuppliers, useSupplierProducts } from '../../../api/useSuppliers';
import { Button } from '../../../components/ui/Button';
import { Modal } from '../../../components/ui/Modal';
import { EmptyState } from '../../../components/ui/EmptyState';
import { formatCurrency } from '../../../utils/format';
import { effectivePrice, marginPct, marginColor } from '../../../utils/expense';

const GUESS_SERVICE_TYPE: Record<string, ServiceType> = {
  'album': 'ALBUM', 'stampa': 'STAMPA_FINE_ART', 'poster': 'POSTER',
  'video': 'VIDEO_MATRIMONIO', 'trailer': 'TRAILER', 'reel': 'REEL',
  'drone': 'DRONE', 'usb': 'USB_PERSONALIZZATA', 'shooting': 'SHOOTING_PRE',
};
function guessServiceType(category?: string): ServiceType | undefined {
  if (!category) return undefined;
  const lower = category.toLowerCase();
  for (const [key, value] of Object.entries(GUESS_SERVICE_TYPE)) {
    if (lower.includes(key)) return value;
  }
  return undefined;
}
import { AttributeEditor, type AttributeDef } from '../../../components/ui/AttributeEditor';

export function parseOptions(opts: string | null | undefined): string[] {
  if (!opts) return [];
  const s = opts.trim();
  if (s.startsWith('[')) { try { return JSON.parse(s) as string[]; } catch { /* fall through */ } }
  return s.split('\n').map(o => o.trim()).filter(Boolean);
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SERVICE_TYPES: { value: ServiceType; label: string; emoji: ReactNode }[] = [
  { value: 'REPORTAGE_FOTO',      label: 'Reportage fotografico',  emoji: <Camera size={16} /> },
  { value: 'ALBUM',               label: 'Album fotografico',       emoji: <BookOpen size={16} /> },
  { value: 'MINI_ALBUM_GENITORI', label: 'Mini album genitori',     emoji: <Book size={16} /> },
  { value: 'VIDEO_MATRIMONIO',    label: 'Film matrimonio',         emoji: <Clapperboard size={16} /> },
  { value: 'TRAILER',             label: 'Trailer',                 emoji: <Film size={16} /> },
  { value: 'REEL',                label: 'Reel social',             emoji: <Smartphone size={16} /> },
  { value: 'SDE',                 label: 'SDE — Same Day Edit',     emoji: <Zap size={16} /> },
  { value: 'DRONE',               label: 'Riprese drone',           emoji: <Radio size={16} /> },
  { value: 'SHOOTING_PRE',        label: 'Shooting pre-evento',     emoji: <Heart size={16} /> },
  { value: 'SHOOTING_POST',       label: 'Shooting post-evento',    emoji: <Sunset size={16} /> },
  { value: 'STAMPA_RISTORANTE',   label: 'Stampa ristorante',       emoji: <Printer size={16} /> },
  { value: 'POSTER',              label: 'Poster',                  emoji: <Image size={16} /> },
  { value: 'STAMPA_FINE_ART',     label: 'Stampa fine art',         emoji: <Palette size={16} /> },
  { value: 'USB_PERSONALIZZATA',  label: 'USB personalizzata',      emoji: <Save size={16} /> },
  { value: 'SECONDO_FOTOGRAFO',   label: 'Secondo fotografo',       emoji: <User size={16} /> },
  { value: 'VIDEOMAKER',          label: 'Videomaker',              emoji: <Video size={16} /> },
  { value: 'ALTRO',               label: 'Altro',                   emoji: <Circle size={16} /> },
];

const svcLabel = (t: ServiceType) => SERVICE_TYPES.find(s => s.value === t)?.label ?? t;
const svcEmoji = (t: ServiceType): ReactNode =>
  SERVICE_TYPES.find(s => s.value === t)?.emoji ?? <Circle size={16} />;

// ─── Package grouping helpers ─────────────────────────────────────────────────

interface PackageGroup {
  packageId: string;
  packageName: string;
  discountPercentage?: number | null;
  discountLabel?: string | null;
  items: EventService[];
}

function groupServicesByPackage(
  services: EventService[],
  packages: ServicePackage[]
): { groups: PackageGroup[]; ungrouped: EventService[] } {
  const pkgMap = new Map<string, PackageGroup>();
  const ungrouped: EventService[] = [];

  for (const svc of services) {
    if (!svc.packageId) {
      ungrouped.push(svc);
      continue;
    }
    if (!pkgMap.has(svc.packageId)) {
      const pkg = packages.find(p => p.id === svc.packageId);
      pkgMap.set(svc.packageId, {
        packageId: svc.packageId,
        packageName: pkg?.name ?? 'Pacchetto',
        discountPercentage: pkg?.discountPercentage,
        discountLabel: pkg?.discountLabel,
        items: [],
      });
    }
    pkgMap.get(svc.packageId)!.items.push(svc);
  }

  return { groups: Array.from(pkgMap.values()), ungrouped };
}

// ─── Shared input styles ──────────────────────────────────────────────────────

const inpSt: React.CSSProperties = {
  background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 'var(--radius-card)', color: 'var(--color-text)',
  fontSize: 'var(--text-sm)', padding: '0.4rem 0.625rem', outline: 'none', width: '100%',
};
const selSt: React.CSSProperties = { ...inpSt, cursor: 'pointer' };
const iconBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--color-text-muted)', padding: '0.25rem', display: 'flex',
  alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--radius-interactive)',
};

// ─── OptionsPanel ─────────────────────────────────────────────────────────────

function OptionsPanel({
  serviceType, options, onChange,
}: {
  serviceType: ServiceType;
  options: Record<string, unknown>;
  onChange: (opts: Record<string, unknown>) => void;
}) {
  const upd = (key: string, val: unknown) => onChange({ ...options, [key]: val });
  const f   = (key: string, def = '') => (options[key] as string) ?? def;
  const n   = (key: string) => options[key] != null ? String(options[key]) : '';

  const inp = (label: string, key: string, type = 'text', placeholder = '') => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }} key={key}>
      <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 500 }}>{label}</label>
      <input type={type} value={type === 'number' ? n(key) : f(key)} placeholder={placeholder}
        onChange={e => upd(key, type === 'number' ? (e.target.value ? Number(e.target.value) : '') : e.target.value)}
        style={inpSt} />
    </div>
  );

  const sel = (label: string, key: string, opts: string[]) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }} key={key}>
      <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 500 }}>{label}</label>
      <select value={f(key)} onChange={e => upd(key, e.target.value)} style={selSt}>
        <option value="">— scegli —</option>
        {opts.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  const g = (children: React.ReactNode[]) => (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(children.filter(Boolean).length, 3)}, 1fr)`, gap: '0.5rem' }}>
      {children}
    </div>
  );

  const panelMap: Partial<Record<ServiceType, React.ReactNode>> = {
    ALBUM: g([sel('Dimensione', 'dimension', ['20x20','25x25','30x30','35x35','40x40','Personalizzata']), inp('N. pagine', 'pages', 'number', 'es. 60'), inp('Copertina', 'cover', 'text', 'es. Pelle nera')]),
    MINI_ALBUM_GENITORI: g([sel('Dimensione', 'dimension', ['15x15','20x20','25x25','30x30']), inp('Quantità', 'quantity', 'number', 'es. 2')]),
    STAMPA_RISTORANTE: g([inp('Quantità', 'quantity', 'number', 'es. 30'), inp('Dimensione', 'dimension', 'text', 'es. 15x20'), inp('Modello', 'model', 'text', 'es. Standard')]),
    POSTER: g([inp('Quantità', 'quantity', 'number', 'es. 1'), inp('Dimensione', 'dimension', 'text', 'es. 50x70')]),
    DRONE: inp('Ore di volo', 'hours', 'number', 'es. 2'),
    SHOOTING_PRE: g([inp('Durata (ore)', 'durationHours', 'number', 'es. 2'), inp('Location', 'location', 'text', 'es. Centro storico')]),
    SHOOTING_POST: g([inp('Durata (ore)', 'durationHours', 'number', 'es. 2'), inp('Location', 'location', 'text', 'opzionale')]),
    VIDEO_MATRIMONIO: inp('Durata stimata (min)', 'durationMin', 'number', 'es. 25'),
    TRAILER: inp('Durata stimata (min)', 'durationMin', 'number', 'es. 4'),
    REEL: inp('Durata stimata (sec)', 'durationSec', 'number', 'es. 90'),
    SDE: inp('Durata stimata (min)', 'durationMin', 'number', 'es. 6'),
  };

  const panel = panelMap[serviceType];
  if (!panel) return null;

  return (
    <div style={{ marginTop: '0.625rem', padding: '0.625rem', background: 'var(--color-surface-3)', borderRadius: 'var(--radius-interactive)', border: '1px dashed rgba(255,255,255,0.10)' }}>
      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginBottom: '0.5rem', fontWeight: 600 }}>
        Opzioni — {svcLabel(serviceType)}
      </p>
      {panel}
    </div>
  );
}

// ─── ServiceRow ───────────────────────────────────────────────────────────────

interface ServiceRowProps {
  es: EventService;
  eventId: string;
  isFirst: boolean;
  isLast: boolean;
  template?: ProductTemplate | null;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDeleted: () => void;
}

function ServiceRow({ es, eventId, isFirst, isLast, template, onMoveUp, onMoveDown, onDeleted }: ServiceRowProps) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    serviceType: es.serviceType,
    label: es.label,
    price: String(es.unitPrice),
    cost: es.unitCost != null ? String(es.unitCost) : '',
    quantity: String(es.quantity ?? 1),
    discount: es.discount != null ? String(es.discount) : '',
    discountType: (es.discountType ?? 'PERCENTAGE') as string,
    options: (es.options as Record<string, unknown>) ?? {},
    attributeValues: (es.attributeValues as Record<string, unknown>) ?? {},
    notes: es.notes ?? '',
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['event', eventId] });

  const updateMutation = useMutation({
    mutationFn: () => apiPatch(`/events/${eventId}/services/${es.id}`, {
      serviceType: form.serviceType,
      label: form.label,
      unitPrice: Number(form.price) || 0,
      unitCost: form.cost !== '' ? Number(form.cost) : null,
      quantity: Math.max(1, Number(form.quantity) || 1),
      discount: form.discount !== '' ? Number(form.discount) : null,
      discountType: form.discount !== '' ? form.discountType : null,
      options: Object.keys(form.options).some(k => form.options[k] !== '' && form.options[k] != null)
        ? form.options : null,
      attributeValues: Object.keys(form.attributeValues).length ? form.attributeValues : null,
      notes: form.notes || null,
    }),
    onSuccess: () => { invalidate(); setEditing(false); toast.success('Servizio aggiornato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiDelete(`/events/${eventId}/services/${es.id}`),
    onSuccess: () => { invalidate(); onDeleted(); toast.success('Servizio rimosso'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const net = effectivePrice(es);
  const hasDiscount = es.discountType != null && es.discount != null;

  if (editing) {
    return (
      <div style={{
        border: '1px solid var(--color-primary)', borderRadius: 'var(--radius-card)',
        padding: '1rem', background: 'var(--glass-bg-card)', display: 'flex', flexDirection: 'column', gap: '0.75rem',
      }}>
        {/* Row 1: type + label + price + quantity */}
        <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr 120px 80px', gap: '0.5rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Tipo</label>
            <select
              value={form.serviceType}
              onChange={e => setForm(f => ({ ...f, serviceType: e.target.value as ServiceType, options: {} }))}
              style={selSt}
            >
              {SERVICE_TYPES.map(s => <option key={s.value} value={s.value}>{s.emoji} {s.label}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Label (descrizione)</label>
            <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} style={inpSt} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Prezzo €</label>
            <input type="number" min="0" step="0.01" value={form.price}
              onChange={e => setForm(f => ({ ...f, price: e.target.value }))} style={inpSt} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Costo €</label>
            <input type="number" min="0" step="0.01" placeholder="0" value={form.cost}
              onChange={e => setForm(f => ({ ...f, cost: e.target.value }))} style={inpSt} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Qtà</label>
            <input type="number" min="1" step="1" value={form.quantity}
              onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} style={inpSt} />
          </div>
        </div>

        {/* Row 2: discount */}
        <div style={{ display: 'grid', gridTemplateColumns: '140px 120px 1fr', gap: '0.5rem', alignItems: 'end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Tipo sconto</label>
            <select value={form.discountType} onChange={e => setForm(f => ({ ...f, discountType: e.target.value }))} style={selSt}>
              <option value="PERCENTAGE">% Percentuale</option>
              <option value="FIXED">€ Importo fisso</option>
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>
              {form.discountType === 'PERCENTAGE' ? 'Sconto (%)' : 'Sconto (€)'} — opzionale
            </label>
            <input type="number" min="0" step={form.discountType === 'PERCENTAGE' ? '1' : '0.01'}
              max={form.discountType === 'PERCENTAGE' ? '100' : undefined}
              placeholder="nessuno" value={form.discount}
              onChange={e => setForm(f => ({ ...f, discount: e.target.value }))} style={inpSt} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Note interne (opzionale)</label>
            <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="es. include consegna digitale" style={inpSt} />
          </div>
        </div>

        {/* Options panel */}
        <OptionsPanel
          serviceType={form.serviceType}
          options={form.options}
          onChange={opts => setForm(f => ({ ...f, options: opts }))}
        />

        {/* Attribute values panel (catalog or supplier) */}
        {template && (
          <AttributeValuesEditor
            template={template}
            values={form.attributeValues}
            onChange={av => setForm(f => ({ ...f, attributeValues: av }))}
          />
        )}
        {!template && (es.supplierProduct?.attributes ?? []).length > 0 && (
          <AttributeEditor
            attrs={(es.supplierProduct!.attributes ?? []).map(a => ({
              key: a.key, attributeType: a.attributeType,
              defaultValue: a.defaultValue, options: a.options,
              unit: a.unit, isRequired: a.isRequired,
            }))}
            values={form.attributeValues}
            onChange={av => setForm(f => ({ ...f, attributeValues: av as Record<string, unknown> }))}
          />
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Annulla</Button>
          <Button size="sm" onClick={() => updateMutation.mutate()} loading={updateMutation.isPending}
            disabled={!form.label.trim()}>
            Salva
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.5rem',
      padding: '0.75rem 0.875rem',
      background: 'var(--glass-bg-card)',
      border: '1px solid rgba(255,255,255,0.10)',
      borderRadius: 'var(--radius-card)',
      transition: 'background var(--transition-interactive)',
    }}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--glass-bg-card)')}
      onMouseLeave={e => (e.currentTarget.style.background = 'var(--glass-bg-card)')}
    >
      {/* Reorder */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', flexShrink: 0 }}>
        <button onClick={onMoveUp} disabled={isFirst} style={{ ...iconBtn, opacity: isFirst ? 0.25 : 0.6 }} title="Sposta su"><ArrowUp size={13} /></button>
        <button onClick={onMoveDown} disabled={isLast} style={{ ...iconBtn, opacity: isLast ? 0.25 : 0.6 }} title="Sposta giù"><ArrowDown size={13} /></button>
      </div>

      {/* Icon — usa icona catalogo se disponibile */}
      <span style={{ fontSize: '1.1rem', flexShrink: 0, display: 'flex', alignItems: 'center', color: 'var(--color-text-muted)' }}>
        {es.supplierId ? <Truck size={16} /> : template?.icon ? renderCatalogIcon(template.icon, 18) : svcEmoji(es.serviceType)}
      </span>

      {/* Label + type */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 'var(--text-sm)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {es.label}
          {(es.quantity ?? 1) > 1 && (
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginLeft: '0.375rem' }}>
              ×{es.quantity}
            </span>
          )}
        </p>
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginTop: '1px' }}>
          {es.supplierId ? (
            <>Fornitore: {es.supplier?.studioName ?? `${es.supplier?.firstName} ${es.supplier?.lastName}`} · {es.supplierProduct?.name}</>
          ) : (
            <>{svcLabel(es.serviceType)}</>
          )}
          {es.notes && <> · <span style={{ fontStyle: 'italic' }}>{es.notes}</span></>}
        </p>
        {es.attributeValues && Object.keys(es.attributeValues as Record<string,unknown>).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.25rem' }}>
            {Object.entries(es.attributeValues as Record<string, unknown>).map(([k, v]) =>
              v !== '' && v != null ? (
                <span key={k} style={{
                  fontSize: '10px', padding: '0.0625rem 0.375rem',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  borderRadius: 'var(--radius-interactive)',
                  color: 'var(--color-text-muted)',
                }}>
                  {k}: {String(v)}
                </span>
              ) : null
            )}
          </div>
        )}
        {es.supplierProduct?.attributes && (!es.attributeValues || Object.keys(es.attributeValues as Record<string, unknown>).length === 0) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.25rem' }}>
            {es.supplierProduct.attributes.filter(a => a.key).map(a => (
              <span key={a.key} style={{ fontSize: '10px', padding: '0.0625rem 0.375rem', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 'var(--radius-interactive)', color: 'var(--color-text-faint)' }}>
                {a.key}{a.defaultValue ? `: ${a.defaultValue}` : ''}{a.unit ? ` ${a.unit}` : ''}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Cost + margin (when cost is tracked) */}
      {(() => {
        const totalCostRow = (es.supplierCostOverride ?? es.unitCost ?? 0);
        if (totalCostRow <= 0) return null;
        const qty = es.quantity ?? 1;
        const netP = effectivePrice(es) * qty;
        const fullCost = totalCostRow * qty;
        const mPct = marginPct(netP, fullCost);
        return (
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>
              costo {formatCurrency(fullCost)}
            </p>
            <span style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: marginColor(mPct) }}>
              {mPct}%
            </span>
          </div>
        );
      })()}

      {/* Price area */}
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        {hasDiscount && (
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', textDecoration: 'line-through' }}>
            {formatCurrency(es.unitPrice * (es.quantity ?? 1))}
          </p>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
          {hasDiscount && (
            <span style={{
              fontSize: '10px', fontWeight: 700,
              color: 'var(--color-primary)',
              background: 'oklch(from var(--color-primary) l c h / 0.12)',
              padding: '0.0625rem 0.375rem', borderRadius: 'var(--radius-interactive)',
            }}>
              {es.discountType === 'PERCENTAGE' ? `−${es.discount}%` : `−${formatCurrency(es.discount ?? 0)}`}
            </span>
          )}
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--color-gold)' }}>
            {formatCurrency(net * (es.quantity ?? 1))}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '0.125rem', flexShrink: 0 }}>
        <button
          onClick={() => {
            setForm({ serviceType: es.serviceType, label: es.label, price: String(es.unitPrice),
              cost: es.unitCost != null ? String(es.unitCost) : '',
              quantity: String(es.quantity ?? 1),
              discount: es.discount != null ? String(es.discount) : '', discountType: es.discountType ?? 'PERCENTAGE',
              options: (es.options as Record<string, unknown>) ?? {}, attributeValues: (es.attributeValues as Record<string, unknown>) ?? {}, notes: es.notes ?? '' });
            setEditing(true);
          }}
          style={iconBtn} title="Modifica"
        >
          <Pencil size={14} />
        </button>
        <button
          onClick={() => { if (confirm(`Rimuovere "${es.label}"?`)) deleteMutation.mutate(); }}
          disabled={deleteMutation.isPending}
          style={{ ...iconBtn, color: 'var(--color-danger)' }} title="Elimina"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// ─── ApplyPackageModal ────────────────────────────────────────────────────────

interface PkgItemPrice {
  id: string; label: string; basePrice: number; price: string;
  serviceType: ServiceType;
}

function ApplyPackageModal({
  open, onClose, eventId,
}: {
  open: boolean; onClose: () => void; eventId: string;
}) {
  const qc = useQueryClient();
  const [selectedPkgId, setSelectedPkgId] = useState('');
  const [items, setItems] = useState<PkgItemPrice[]>([]);
  const [applyDiscount, setApplyDiscount] = useState(true);

  const { data: packages = [] } = useQuery<ServicePackage[]>({
    queryKey: ['service-packages'],
    queryFn: () => apiGet<ServicePackage[]>('/service-packages'),
    enabled: open,
  });

  const selectedPkg = packages.find(p => p.id === selectedPkgId) ?? null;
  const hasDiscount = !!selectedPkg?.discountPercentage && selectedPkg.discountPercentage > 0;

  const selectPackage = (pkgId: string) => {
    setSelectedPkgId(pkgId);
    setApplyDiscount(true);
    const pkg = packages.find(p => p.id === pkgId);
    if (!pkg) { setItems([]); return; }
    setItems(pkg.items.map(item => ({
      id: item.id,
      label: item.label,
      basePrice: item.priceOverride ?? item.basePrice,
      price: String(item.priceOverride ?? item.basePrice),
      serviceType: item.serviceType,
    })));
  };

  const applyMutation = useMutation({
    mutationFn: () => {
      const priceOverrides: Record<string, number> = {};
      for (const item of items) {
        const parsed = Number(item.price);
        if (!isNaN(parsed) && parsed !== item.basePrice) {
          priceOverrides[item.id] = parsed;
        }
      }
      return apiPost(`/events/${eventId}/apply-package`, {
        packageId: selectedPkgId,
        priceOverrides: Object.keys(priceOverrides).length ? priceOverrides : undefined,
        applyDiscount,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event', eventId] });
      toast.success(`${items.length} servizi aggiunti dal pacchetto`);
      handleClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const handleClose = () => {
    setSelectedPkgId('');
    setItems([]);
    setApplyDiscount(true);
    onClose();
  };

  const activePackages = packages.filter(p => p.isActive);

  // Calcola preview prezzi con eventuale sconto
  const discountMultiplier = applyDiscount && hasDiscount ? 1 - (selectedPkg!.discountPercentage! / 100) : 1;
  const totalBase = items.reduce((s, i) => s + Number(i.price), 0);
  const totalNet = totalBase * discountMultiplier;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Applica pacchetto servizi"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose}>Annulla</Button>
          <Button onClick={() => applyMutation.mutate()} loading={applyMutation.isPending} disabled={!selectedPkgId || items.length === 0}>
            Aggiungi {items.length > 0 ? `${items.length} servizi` : 'servizi'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <label style={{ fontSize: 'var(--text-sm)', fontWeight: 600, display: 'block', marginBottom: '0.375rem' }}>
            Seleziona pacchetto
          </label>
          {activePackages.length === 0 ? (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
              Nessun pacchetto attivo. Creane uno in Impostazioni → Pacchetti Servizi.
            </p>
          ) : (
            <select
              value={selectedPkgId}
              onChange={e => selectPackage(e.target.value)}
              style={{ ...selSt, fontSize: 'var(--text-sm)' }}
            >
              <option value="">— seleziona —</option>
              {activePackages.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name}{p.eventType ? ` (${p.eventType})` : ''} · {p.items.length} servizi
                  {p.discountPercentage ? ` · −${p.discountPercentage}%` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        {items.length > 0 && (
          <>
            {hasDiscount && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                padding: '0.625rem 0.875rem',
                background: 'oklch(from var(--color-primary) l c h / 0.08)',
                border: '1px solid oklch(from var(--color-primary) l c h / 0.3)',
                borderRadius: 'var(--radius-card)',
              }}>
                <Tag size={14} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                <span style={{ fontSize: 'var(--text-sm)', flex: 1, color: 'var(--color-primary)', fontWeight: 500 }}>
                  {selectedPkg!.discountLabel ?? 'Sconto pacchetto'}: −{selectedPkg!.discountPercentage}%
                </span>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', cursor: 'pointer', fontSize: 'var(--text-xs)' }}>
                  <input type="checkbox" checked={applyDiscount} onChange={e => setApplyDiscount(e.target.checked)} />
                  Applica
                </label>
              </div>
            )}

            <div>
              <p style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: '0.5rem' }}>
                Servizi inclusi — modifica i prezzi se necessario:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', maxHeight: '260px', overflowY: 'auto' }}>
                {items.map((item, idx) => {
                  const finalPrice = Number(item.price) * discountMultiplier;
                  return (
                    <div key={item.id} style={{
                      display: 'flex', alignItems: 'center', gap: '0.625rem',
                      padding: '0.5rem 0.75rem', background: 'var(--glass-bg-card)',
                      border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)',
                    }}>
                      <span style={{ fontSize: '0.9rem', flexShrink: 0 }}>{svcEmoji(item.serviceType)}</span>
                      <span style={{ flex: 1, fontSize: 'var(--text-sm)', fontWeight: 500 }}>{item.label}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0 }}>
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>€</span>
                        <input
                          type="number" min="0" step="0.01"
                          value={item.price}
                          onChange={e => {
                            const newItems = [...items];
                            newItems[idx] = { ...item, price: e.target.value };
                            setItems(newItems);
                          }}
                          style={{ ...inpSt, width: '90px', textAlign: 'right' }}
                        />
                      </div>
                      {applyDiscount && hasDiscount && (
                        <span style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--color-gold)', flexShrink: 0 }}>
                          → {formatCurrency(finalPrice)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Totale preview */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              paddingTop: '0.5rem', borderTop: '1px solid rgba(255,255,255,0.08)',
            }}>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>Totale</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {applyDiscount && hasDiscount && totalBase !== totalNet && (
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', textDecoration: 'line-through' }}>
                    {formatCurrency(totalBase)}
                  </span>
                )}
                <span style={{ fontSize: 'var(--text-md)', fontWeight: 700, color: 'var(--color-gold)' }}>
                  {formatCurrency(totalNet)}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ─── AttributeValuesEditor (inline) ──────────────────────────────────────────

function AttributeValuesEditor({
  template, values, onChange,
}: {
  template: ProductTemplate;
  values: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void;
}) {
  const attrs: AttributeDef[] = (template.attributes ?? []).map(a => ({
    key: a.key,
    attributeType: a.attributeType,
    defaultValue: a.defaultValue,
    options: a.options,
    unit: a.unit,
    isRequired: a.isRequired,
  }));
  return <AttributeEditor attrs={attrs} values={values} onChange={onChange} />;
}

// ─── AddServiceForm ───────────────────────────────────────────────────────────

function AddServiceForm({
  eventId, sortOrder, onAdded,
}: {
  eventId: string; sortOrder: number; onAdded: () => void;
}) {
  const qc = useQueryClient();
  const { data: categories = [] } = useProductCategories();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'catalog' | 'custom' | 'supplier'>('catalog');

  // Catalog mode state
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [catalogForm, setCatalogForm] = useState({
    label: '',
    price: '',
    cost: '',
    quantity: '1',
    attributeValues: {} as Record<string, unknown>,
  });

  // Custom mode state
  const [customForm, setCustomForm] = useState({
    serviceType: 'REPORTAGE_FOTO' as ServiceType,
    label: '',
    price: '',
    cost: '',
    quantity: '1',
    options: {} as Record<string, unknown>,
  });

  // Supplier mode state
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [selectedSupplierProductId, setSelectedSupplierProductId] = useState('');
  const [supplierForm, setSupplierForm] = useState({
    label: '',
    price: '',
    cost: '',
    quantity: '1',
    attributeValues: {} as Record<string, unknown>,
  });

  const { data: suppliers = [] } = useQuery<Supplier[]>({
    queryKey: ['suppliers-list'],
    queryFn: async () => {
      const res = await apiGet<{ data: Supplier[] }>('/suppliers?active=true&limit=200');
      return res?.data ?? [];
    },
    enabled: open,
  });

  // Catalog supplier state
  const [catalogSupplierId, setCatalogSupplierId] = useState('');
  const [catalogSupplierProductId, setCatalogSupplierProductId] = useState('');
  const [catalogSupplierCost, setCatalogSupplierCost] = useState('');

  const { data: catalogSupplierProducts = [] } = useQuery<SupplierProduct[]>({
    queryKey: ['catalog-supplier-products', catalogSupplierId],
    queryFn: () => apiGet<SupplierProduct[]>(`/suppliers/${catalogSupplierId}/products`),
    enabled: open && !!catalogSupplierId,
  });

  const { data: supplierProducts = [] } = useQuery<SupplierProduct[]>({
    queryKey: ['supplier-products', selectedSupplierId],
    queryFn: () => apiGet<SupplierProduct[]>(`/suppliers/${selectedSupplierId}/products`),
    enabled: open && !!selectedSupplierId,
  });

  const selectedSupplierProduct = supplierProducts.find(p => p.id === selectedSupplierProductId) ?? null;

  const { data: templates = [] } = useQuery<ProductTemplate[]>({
    queryKey: ['product-templates'],
    queryFn: () => apiGet<ProductTemplate[]>('/product-templates'),
    enabled: open,
  });

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId) ?? null;

  const handleTemplateChange = (id: string) => {
    setSelectedTemplateId(id);
    const tpl = templates.find(t => t.id === id);
    if (tpl) {
      const defaultAttrValues: Record<string, unknown> = {};
      tpl.attributes?.forEach(a => {
        if (a.defaultValue != null) {
          defaultAttrValues[a.key] = a.attributeType === 'BOOLEAN'
            ? a.defaultValue === 'true'
            : a.defaultValue;
        }
      });
      setCatalogForm({ label: tpl.name, price: String(tpl.basePrice), cost: String(tpl.baseCost), quantity: '1', attributeValues: defaultAttrValues });
    }
  };

  const handleSupplierProductChange = (productId: string) => {
    setSelectedSupplierProductId(productId);
    const sp = supplierProducts.find(p => p.id === productId);
    if (sp) {
      const defaultAttrValues: Record<string, unknown> = {};
      sp.attributes?.forEach(a => {
        if (a.defaultValue != null && a.key) {
          defaultAttrValues[a.key] = a.attributeType === 'BOOLEAN'
            ? a.defaultValue === 'true'
            : a.attributeType === 'NUMBER'
              ? Number(a.defaultValue)
              : a.defaultValue;
        } else if (a.key) {
          defaultAttrValues[a.key] = '';
        }
      });
      setSupplierForm({
        label: sp.name,
        price: '',
        cost: String(sp.unitCost),
        quantity: '1',
        attributeValues: defaultAttrValues,
      });
    }
  };

  const selectedSupplierProductData = supplierProducts.find(p => p.id === selectedSupplierProductId) ?? null;

  const addMutation = useMutation({
    mutationFn: () => {
      if (mode === 'catalog' && selectedTemplate) {
        return apiPost(`/events/${eventId}/services`, {
          serviceType: resolveServiceType(selectedTemplate, categories),
          label: catalogForm.label || selectedTemplate.name,
          unitPrice: catalogForm.price !== '' ? Number(catalogForm.price) : selectedTemplate.basePrice,
          unitCost: catalogForm.cost !== '' ? Number(catalogForm.cost) : selectedTemplate.baseCost,
          quantity: Math.max(1, Number(catalogForm.quantity) || 1),
          productTemplateId: selectedTemplate.id,
          attributeValues: Object.keys(catalogForm.attributeValues).length ? catalogForm.attributeValues : null,
          sortOrder,
          ...(catalogSupplierProductId ? {
            supplierProductId: catalogSupplierProductId,
            supplierCostOverride: catalogSupplierCost !== '' ? Number(catalogSupplierCost) : undefined,
          } : {}),
        });
      } else if (mode === 'supplier' && selectedSupplierProductId) {
        const sp = supplierProducts.find(p => p.id === selectedSupplierProductId);
        return apiPost(`/events/${eventId}/services`, {
          serviceType: (sp?.serviceType || guessServiceType(sp?.category) || 'ALTRO') as ServiceType,
          label: supplierForm.label || sp?.name || 'Prodotto fornitore',
          unitPrice: supplierForm.price !== '' ? Number(supplierForm.price) : 0,
          unitCost: null,
          quantity: Math.max(1, Number(supplierForm.quantity) || 1),
          supplierProductId: selectedSupplierProductId,
          supplierCostOverride: supplierForm.cost !== '' ? Number(supplierForm.cost) : undefined,
          attributeValues: Object.keys(supplierForm.attributeValues).length ? supplierForm.attributeValues : null,
          sortOrder,
        });
      } else {
        return apiPost(`/events/${eventId}/services`, {
          serviceType: customForm.serviceType,
          label: customForm.label,
          unitPrice: Number(customForm.price) || 0,
          unitCost: customForm.cost !== '' ? Number(customForm.cost) : null,
          quantity: Math.max(1, Number(customForm.quantity) || 1),
          sortOrder,
          options: Object.keys(customForm.options).some(k => customForm.options[k] !== '' && customForm.options[k] != null)
            ? customForm.options : null,
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event', eventId] });
      setSelectedTemplateId('');
      setCatalogForm({ label: '', price: '', cost: '', quantity: '1', attributeValues: {} });
      setCustomForm({ serviceType: 'REPORTAGE_FOTO', label: '', price: '', cost: '', quantity: '1', options: {} });
      setSelectedSupplierId('');
      setSelectedSupplierProductId('');
      setSupplierForm({ label: '', price: '', cost: '', quantity: '1', attributeValues: {} });
      setCatalogSupplierId('');
      setCatalogSupplierProductId('');
      setCatalogSupplierCost('');
      setOpen(false);
      onAdded();
      toast.success('Servizio aggiunto');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const catalogValid = mode === 'catalog' && !!selectedTemplateId && !!catalogForm.label.trim();
  const customValid = mode === 'custom' && !!customForm.label.trim();
  const supplierValid = mode === 'supplier' && !!selectedSupplierProductId && !!supplierForm.label.trim();

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.375rem',
          width: '100%', padding: '0.625rem 0.875rem',
          background: 'none', border: '1px dashed rgba(255,255,255,0.10)',
          borderRadius: 'var(--radius-card)', cursor: 'pointer',
          color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)',
          transition: 'all var(--transition-interactive)',
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-primary)'; e.currentTarget.style.color = 'var(--color-primary)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)'; e.currentTarget.style.color = 'var(--color-text-muted)'; }}
      >
        <Plus size={15} />
        Aggiungi servizio
      </button>
    );
  }

  return (
    <div style={{
      border: '1px solid var(--color-primary)', borderRadius: 'var(--radius-card)',
      padding: '1rem', background: 'var(--glass-bg-card)', display: 'flex', flexDirection: 'column', gap: '0.75rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <p style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--color-primary)' }}>Nuovo servizio</p>
        {/* Mode tabs */}
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          {(['catalog', 'custom', 'supplier'] as const).map(m => (
            <button key={m} onClick={() => setMode(m)} style={{
              background: mode === m ? 'var(--color-primary-highlight)' : 'none',
              border: `1px solid ${mode === m ? 'var(--color-primary)' : 'rgba(255,255,255,0.10)'}`,
              borderRadius: 'var(--radius-interactive)', padding: '0.2rem 0.625rem',
              color: mode === m ? 'var(--color-primary)' : 'var(--color-text-muted)',
              fontSize: 'var(--text-xs)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem',
            }}>
              {m === 'catalog' ? <><BookOpen size={11} /> Dal catalogo</>
               : m === 'supplier' ? <><Truck size={11} /> Da Fornitore</>
               : <><Plus size={11} /> Personalizzato</>}
            </button>
          ))}
        </div>
      </div>

      {mode === 'catalog' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
          <div>
            <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Prodotto dal catalogo *</label>
            <select value={selectedTemplateId} onChange={e => handleTemplateChange(e.target.value)} style={{ ...selSt, marginTop: '0.25rem' }}>
              <option value="">— seleziona prodotto —</option>
              {templates.filter(t => t.isActive).map(t => (
                <option key={t.id} value={t.id}>
                  {renderCatalogIcon(resolveCategoryIcon(t, categories), 18)} {t.name} (€ {t.basePrice.toFixed(2)})
                </option>
              ))}
            </select>
            {templates.length === 0 && (
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginTop: '0.25rem' }}>
                Nessun prodotto in catalogo. Creane uno in Impostazioni → Catalogo Prodotti.
              </p>
            )}
          </div>
          {selectedTemplate && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px 72px', gap: '0.5rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Label *</label>
                  <input value={catalogForm.label} onChange={e => setCatalogForm(f => ({ ...f, label: e.target.value }))}
                    placeholder={selectedTemplate.name} style={inpSt} autoFocus />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Prezzo €</label>
                  <input type="number" min="0" step="0.01"
                    value={catalogForm.price} placeholder={String(selectedTemplate.basePrice)}
                    onChange={e => setCatalogForm(f => ({ ...f, price: e.target.value }))} style={inpSt} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Costo €</label>
                  <input type="number" min="0" step="0.01"
                    value={catalogForm.cost} placeholder={String(selectedTemplate.baseCost)}
                    onChange={e => setCatalogForm(f => ({ ...f, cost: e.target.value }))} style={inpSt} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Qtà</label>
                  <input type="number" min="1" step="1"
                    value={catalogForm.quantity}
                    onChange={e => setCatalogForm(f => ({ ...f, quantity: e.target.value }))} style={inpSt} />
                </div>
              </div>
              {/* Live margin */}
              {(() => {
                const c = catalogForm.cost !== '' ? Number(catalogForm.cost) : selectedTemplate.baseCost;
                const p = catalogForm.price !== '' ? Number(catalogForm.price) : selectedTemplate.basePrice;
                const qty = Math.max(1, Number(catalogForm.quantity) || 1);
                const m = marginPct(p, c);
                return (
                  <div style={{ display: 'flex', gap: '1rem', fontSize: 'var(--text-xs)' }}>
                    <span style={{ color: 'var(--color-text-muted)' }}>Costo: <strong>€ {(c * qty).toFixed(2)}</strong></span>
                    <span style={{ color: 'var(--color-text-muted)' }}>Prezzo: <strong>€ {(p * qty).toFixed(2)}</strong></span>
                    <span style={{ fontWeight: 700, color: marginColor(m) }}>Margine: {m}%</span>
                    {qty > 1 && <span style={{ color: 'var(--color-text-faint)' }}>×{qty}</span>}
                  </div>
                );
              })()}
              <AttributeValuesEditor
                template={selectedTemplate}
                values={catalogForm.attributeValues}
                onChange={av => setCatalogForm(f => ({ ...f, attributeValues: av }))}
              />
              {/* Optional supplier for catalog product */}
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '0.625rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Fornitore (opzionale)</label>
                    <select value={catalogSupplierId} onChange={e => { setCatalogSupplierId(e.target.value); setCatalogSupplierProductId(''); setCatalogSupplierCost(''); }} style={selSt}>
                      <option value="">— nessuno —</option>
                      {suppliers.filter(s => s.active).map(s => (
                        <option key={s.id} value={s.id}>{s.studioName ?? `${s.firstName} ${s.lastName}`}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Prodotto fornitore</label>
                    <select value={catalogSupplierProductId} onChange={e => {
                      setCatalogSupplierProductId(e.target.value);
                      const p = catalogSupplierProducts.find(sp => sp.id === e.target.value);
                      setCatalogSupplierCost(p ? String(p.unitCost) : '');
                    }} style={selSt} disabled={!catalogSupplierId}>
                      <option value="">— nessuno —</option>
                      {catalogSupplierProducts.filter(p => p.isActive).map(p => (
                        <option key={p.id} value={p.id}>{p.name} (€{p.unitCost.toFixed(2)})</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {mode === 'custom' && (
        // Custom mode
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr 100px 100px 72px', gap: '0.5rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Tipo</label>
              <select value={customForm.serviceType}
                onChange={e => setCustomForm(f => ({ ...f, serviceType: e.target.value as ServiceType, options: {} }))}
                style={selSt}>
                {SERVICE_TYPES.map(s => <option key={s.value} value={s.value}>{s.emoji} {s.label}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Label *</label>
              <input value={customForm.label} onChange={e => setCustomForm(f => ({ ...f, label: e.target.value }))}
                placeholder="es. Reportage cerimonia + ricevimento" style={inpSt} autoFocus
                onKeyDown={e => { if (e.key === 'Enter' && customForm.label.trim()) addMutation.mutate(); }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Prezzo €</label>
              <input type="number" min="0" step="0.01" placeholder="0" value={customForm.price}
                onChange={e => setCustomForm(f => ({ ...f, price: e.target.value }))} style={inpSt} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Costo €</label>
              <input type="number" min="0" step="0.01" placeholder="0" value={customForm.cost}
                onChange={e => setCustomForm(f => ({ ...f, cost: e.target.value }))} style={inpSt} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Qtà</label>
              <input type="number" min="1" step="1" placeholder="1" value={customForm.quantity}
                onChange={e => setCustomForm(f => ({ ...f, quantity: e.target.value }))} style={inpSt} />
            </div>
          </div>
          <OptionsPanel
            serviceType={customForm.serviceType}
            options={customForm.options}
            onChange={opts => setCustomForm(f => ({ ...f, options: opts }))}
          />
        </>
      )}

      {mode === 'supplier' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Fornitore *</label>
              <select value={selectedSupplierId} onChange={e => { setSelectedSupplierId(e.target.value); setSelectedSupplierProductId(''); setSupplierForm({ label: '', price: '', cost: '', quantity: '1', attributeValues: {} }); }} style={selSt}>
                <option value="">— seleziona fornitore —</option>
                {suppliers.filter(s => s.active).map(s => (
                  <option key={s.id} value={s.id}>{s.studioName ?? `${s.firstName} ${s.lastName}`}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Prodotto *</label>
              <select value={selectedSupplierProductId} onChange={e => handleSupplierProductChange(e.target.value)} style={selSt} disabled={!selectedSupplierId}>
                <option value="">— seleziona prodotto —</option>
                {supplierProducts.filter(p => p.isActive).map(p => (
                  <option key={p.id} value={p.id}>{p.name} (€{p.unitCost.toFixed(2)})</option>
                ))}
              </select>
            </div>
          </div>
          {selectedSupplierProductId && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 120px 72px', gap: '0.5rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Label *</label>
                  <input value={supplierForm.label} onChange={e => setSupplierForm(f => ({ ...f, label: e.target.value }))}
                    placeholder="Nome prodotto" style={inpSt} autoFocus />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Prezzo €</label>
                  <input type="number" min="0" step="0.01" placeholder="0" value={supplierForm.price}
                    onChange={e => setSupplierForm(f => ({ ...f, price: e.target.value }))} style={inpSt} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Costo forn. €</label>
                  <input type="number" min="0" step="0.01" value={supplierForm.cost}
                    onChange={e => setSupplierForm(f => ({ ...f, cost: e.target.value }))} style={inpSt} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Qtà</label>
                  <input type="number" min="1" step="1" placeholder="1" value={supplierForm.quantity}
                    onChange={e => setSupplierForm(f => ({ ...f, quantity: e.target.value }))} style={inpSt} />
                </div>
              </div>
              {/* Live margin */}
              {(() => {
                const c = supplierForm.cost !== '' ? Number(supplierForm.cost) : 0;
                const p = supplierForm.price !== '' ? Number(supplierForm.price) : 0;
                const qty = Math.max(1, Number(supplierForm.quantity) || 1);
                const m = marginPct(p, c);
                return (
                  <div style={{ display: 'flex', gap: '1rem', fontSize: 'var(--text-xs)' }}>
                    <span style={{ color: 'var(--color-text-muted)' }}>Costo: <strong>€ {(c * qty).toFixed(2)}</strong></span>
                    <span style={{ color: 'var(--color-text-muted)' }}>Prezzo: <strong>€ {(p * qty).toFixed(2)}</strong></span>
                    <span style={{ fontWeight: 700, color: marginColor(m) }}>Margine: {m}%</span>
                    {qty > 1 && <span style={{ color: 'var(--color-text-faint)' }}>×{qty}</span>}
                  </div>
                );
              })()}
              {/* Service type info */}
              {selectedSupplierProductData?.serviceType && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                  Tipo: <strong>{svcLabel(selectedSupplierProductData.serviceType)}</strong>
                  {selectedSupplierProductData.unit && <> · {selectedSupplierProductData.unit}</>}
                </div>
              )}
              {/* Editable product attributes from supplier product */}
              <AttributeEditor
                attrs={(selectedSupplierProductData?.attributes ?? []).map(a => ({
                  key: a.key, attributeType: a.attributeType,
                  defaultValue: a.defaultValue, options: a.options,
                  unit: a.unit, isRequired: a.isRequired,
                }))}
                values={supplierForm.attributeValues}
                onChange={(v) => setSupplierForm(f => ({ ...f, attributeValues: v as Record<string, unknown> }))}
              />
            </>
          )}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Annulla</Button>
        <Button size="sm" onClick={() => addMutation.mutate()} loading={addMutation.isPending}
          disabled={!(catalogValid || customValid || supplierValid)} icon={<Check size={14} />}>
          Aggiungi
        </Button>
      </div>
    </div>
  );
}

// ─── ServicesTab ──────────────────────────────────────────────────────────────

export default function ServicesTab({ event }: { event: Event }) {
  const qc = useQueryClient();
  const [showPkgModal, setShowPkgModal] = useState(false);

  const services: EventService[] = [...(event.services ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);

  const { data: packages = [] } = useQuery<ServicePackage[]>({
    queryKey: ['service-packages'],
    queryFn: () => apiGet<ServicePackage[]>('/service-packages'),
  });

  const { data: templates = [] } = useQuery<ProductTemplate[]>({
    queryKey: ['product-templates'],
    queryFn: () => apiGet<ProductTemplate[]>('/product-templates'),
  });

  const { data: categories = [] } = useProductCategories();

  const templateMap = new Map(templates.map(t => [t.id, t]));

  const invalidate = () => qc.invalidateQueries({ queryKey: ['event', event.id] });

  const reorderMutation = useMutation({
    mutationFn: (order: { id: string; sortOrder: number }[]) =>
      apiPut(`/events/${event.id}/services/reorder`, { order }),
    onSuccess: () => invalidate(),
    onError: (e: Error) => toast.error(e.message),
  });

  const moveService = (idx: number, dir: 'up' | 'down') => {
    const next = [...services];
    const swapIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= next.length) return;
    // swap sortOrder values
    const order = next.map((s, i) => {
      if (i === idx) return { id: s.id, sortOrder: next[swapIdx].sortOrder };
      if (i === swapIdx) return { id: s.id, sortOrder: next[idx].sortOrder };
      return { id: s.id, sortOrder: s.sortOrder };
    });
    reorderMutation.mutate(order);
  };

  // Totals — quantity aware
  const gross = services.reduce((s, es) => s + es.unitPrice * (es.quantity ?? 1), 0);
  const net   = services.reduce((s, es) => s + effectivePrice(es) * (es.quantity ?? 1), 0);
  const discount = gross - net;
  const hasDiscounts = services.some(es => es.discountType != null && es.discount != null);
  const totalCost = services.reduce((s, es) => s + (es.unitCost ?? 0) * (es.quantity ?? 1), 0);
  const hasCostTracking = services.some(es => es.unitCost != null);
  const totalMargin = net - totalCost;
  const overallMarginPct = marginPct(net, totalCost);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)', padding: '0.875rem 1rem',
        gap: '0.75rem', flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
          <Package size={18} style={{ color: 'var(--color-text-muted)' }} />
          <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 700 }}>Servizi evento</h3>
          {services.length > 0 && (
            <span style={{
              fontSize: 'var(--text-xs)', background: 'var(--color-surface-3)',
              borderRadius: 'var(--radius-interactive)', padding: '0.125rem 0.5rem',
              color: 'var(--color-text-muted)', fontWeight: 600,
            }}>
              {services.length}
            </span>
          )}
        </div>
        <Button
          size="sm"
          variant="secondary"
          icon={<Package size={14} />}
          onClick={() => setShowPkgModal(true)}
        >
          Applica pacchetto
        </Button>
      </div>

      {/* Empty state */}
      {services.length === 0 && (
        <EmptyState
          icon={<Package size={32} />}
          title="Nessun servizio"
          description="Aggiungi servizi manualmente o applica un pacchetto predefinito"
        />
      )}

      {/* Service list — raggruppato per pacchetto */}
      {services.length > 0 && (() => {
        const { groups, ungrouped } = groupServicesByPackage(services, packages);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {/* Pacchetti */}
            {groups.map(group => {
              const groupNet = group.items.reduce((s, es) => s + effectivePrice(es), 0);
              return (
                <div key={group.packageId} style={{
                  border: '1px solid oklch(from var(--color-primary) l c h / 0.35)',
                  borderRadius: 'var(--radius-card)',
                  overflow: 'hidden',
                }}>
                  {/* Header pacchetto */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.5rem 0.875rem',
                    background: 'oklch(from var(--color-primary) l c h / 0.07)',
                    borderBottom: '1px solid oklch(from var(--color-primary) l c h / 0.2)',
                  }}>
                    <Layers size={13} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                    <span style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--color-primary)', flex: 1 }}>
                      {group.packageName}
                    </span>
                    {group.discountPercentage && group.discountPercentage > 0 && (
                      <span style={{
                        fontSize: '10px', fontWeight: 700, color: 'var(--color-primary)',
                        background: 'oklch(from var(--color-primary) l c h / 0.15)',
                        padding: '0.0625rem 0.375rem', borderRadius: 'var(--radius-interactive)',
                      }}>
                        −{group.discountPercentage}% {group.discountLabel ? `· ${group.discountLabel}` : ''}
                      </span>
                    )}
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gold)', fontWeight: 700 }}>
                      {formatCurrency(groupNet)}
                    </span>
                  </div>
                  {/* Items del pacchetto */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', padding: '0.375rem' }}>
                    {group.items.map((es, idx) => {
                      const globalIdx = services.indexOf(es);
                      return (
                        <ServiceRow
                          key={es.id}
                          es={es}
                          eventId={event.id}
                          isFirst={globalIdx === 0}
                          isLast={globalIdx === services.length - 1}
                          template={es.productTemplateId ? templateMap.get(es.productTemplateId) : null}
                          onMoveUp={() => moveService(globalIdx, 'up')}
                          onMoveDown={() => moveService(globalIdx, 'down')}
                          onDeleted={() => {}}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Servizi aggiuntivi (non da pacchetto) */}
            {ungrouped.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                {groups.length > 0 && (
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', fontWeight: 600, paddingLeft: '0.25rem' }}>
                    Servizi aggiuntivi
                  </p>
                )}
                {ungrouped.map((es) => {
                  const idx = services.indexOf(es);
                  return (
                    <ServiceRow
                      key={es.id}
                      es={es}
                      eventId={event.id}
                      isFirst={idx === 0}
                      isLast={idx === services.length - 1}
                      template={es.productTemplateId ? templateMap.get(es.productTemplateId) : null}
                      onMoveUp={() => moveService(idx, 'up')}
                      onMoveDown={() => moveService(idx, 'down')}
                      onDeleted={() => {}}
                    />
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* Add service form */}
      <AddServiceForm
        eventId={event.id}
        sortOrder={services.length}
        onAdded={() => {}}
      />

      {/* Totals footer */}
      {services.length > 0 && (
        <div style={{
          background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 'var(--radius-card)', padding: '0.875rem 1rem',
          display: 'flex', flexDirection: 'column', gap: '0.375rem',
        }}>
          {hasDiscounts && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>Lordo</span>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-faint)', textDecoration: 'line-through' }}>
                  {formatCurrency(gross)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <Tag size={12} /> Sconti applicati
                </span>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-primary)', fontWeight: 600 }}>
                  −{formatCurrency(discount)}
                </span>
              </div>
            </>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: hasDiscounts ? '0.375rem' : 0, borderTop: hasDiscounts ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
            <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>
              {hasDiscounts ? 'Totale netto' : 'Totale servizi'}
            </span>
            <span style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--color-gold)' }}>
              {formatCurrency(net)}
            </span>
          </div>
          {hasDiscounts && (
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', textAlign: 'right' }}>
              <Percent size={10} style={{ verticalAlign: 'middle', marginRight: '0.25rem' }} />
              Risparmio: {((discount / gross) * 100).toFixed(1)}%
            </p>
          )}
          {hasCostTracking && (
            <>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: '0.25rem', paddingTop: '0.375rem' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Costo totale</span>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{formatCurrency(totalCost)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>Margine lordo</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: marginColor(overallMarginPct), background: 'oklch(from var(--color-success) l c h / 0.1)', padding: '0.1rem 0.375rem', borderRadius: 'var(--radius-interactive)' }}>
                    {overallMarginPct}%
                  </span>
                  <span style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: marginColor(overallMarginPct) }}>
                    {formatCurrency(totalMargin)}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Apply Package modal */}
      <ApplyPackageModal
        open={showPkgModal}
        onClose={() => setShowPkgModal(false)}
        eventId={event.id}
      />
    </div>
  );
}
